import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, AddressElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { GetCartContents,GetAddOns,GetPasses,convertTo12HourFormat,syncCartToServer,GetPaymentIntent, GetClientSecret, GetServiceFees, SetFreeSecret, SubmitFreeOrder, getAllInPrice } from '../api-funcs'; // Assuming GetCartContents is imported from a helper file
import { useCart,usePromoCodeInput, useTimer, EventContext } from '../CartContext';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import useSubdomain from '../useSubdomain';
import { sub } from 'date-fns';


let grandTotal = 0;

const CheckoutForm = ({email,confEmail,customerName,cartInfo,cartID,promoCodeInput,updatePromoCode, formIsValid}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate(); // Hook to navigate
  const [paymentStatus, setPaymentStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const { SetGlobalCartId } = useCart();
  const { stopTimer,resetTimer } = useTimer();
  const { globalEventId } = useContext(EventContext);
  
  //const [isLoading, setIsLoading] = useState(false);
  const subdomain = useSubdomain();


  // Check if payment succeeded
  useEffect(() => {
    
    if (paymentStatus === 'Payment succeeded!') {
      //reset the cart in memory
      SetGlobalCartId({
        cartID: '',
        cartContents: new Map(),
      });

      updatePromoCode(globalEventId,'');

      stopTimer();
      resetTimer();

      localStorage.removeItem('promoCode');
      localStorage.removeItem('cartID');
      //navigate('/order-results');
    }
  }, [paymentStatus, navigate]); // Dependency array, triggers when paymentStatus changes

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (email !== confEmail || submittingRef.current) return;
    submittingRef.current = true;

    setLoading(true);
    setPaymentStatus('');

    if (!stripe || !elements) {
      setPaymentStatus('Stripe is not loaded.');
      submittingRef.current = false;
      setLoading(false);
      return;
    }

    try {
      setLoading(true); // Ensure loading state is set before calling API
      const returnUrl = "https://"+subdomain+".xpresstix.ca/order-results";

      const response = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email: email,
            },
          },
        },
      });
    
      if (response.error) {
        console.error("Payment error:", response.error.message);
        setPaymentStatus(`Payment failed: ${response.error.message}`); // Store error in state
        alert(`Payment failed: ${response.error.message}`); // Show error to user
      } else {
        setPaymentStatus("Payment succeeded!");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setPaymentStatus(`Unexpected error: ${error.message}`);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
    
  
  };

  const paymentElementOptions = {
    layout: "accordion"
  }
  
  return (
    <form onSubmit={handleSubmit}>
    <Container>
      
      <Row className='mt-4'>
        <Col className='m-0 p-0'>
          <PaymentElement id="payment-element" options={paymentElementOptions} />
        </Col>
      </Row>
      <Row className='m-4'>
        <Col>
          <Button type="submit" disabled={loading || !stripe || !elements || !formIsValid} className='btn-atc'>
                  {loading ? 'Processing...' : `Pay $${grandTotal}`}
          </Button>
          <br/><br/>
        </Col>
      </Row>
      <Row>
        <Col  className="secure-text">
          🔒 Secure payments by Stripe. <br/>We never store your credit card details.<br/><br/>
        </Col>
      </Row>
      <Row>
        <Col>
          {paymentStatus && <div>{paymentStatus}</div>}
        </Col>
      </Row>
      </Container>
    </form>
  );
};

const CheckoutPage = () => {
  const [email, setEmail] = useState("");
  const [confEmail, setConfEmail] = useState("");
  const [emailError, setEmailError] = useState("");  // State for error message
  const { globalCartId } = useCart();
  const [cartInfo, setCartInfo] = useState([]);
  const { promoCodeInput, updatePromoCode } = usePromoCodeInput();
  const [clientSecret, setClientSecret] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const { globalEventId } = useContext(EventContext);
  const navigate = useNavigate(); // Hook to navigate
  const { SetGlobalCartId } = useCart();
  const { stopTimer,resetTimer } = useTimer();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { settings } = useContext(EventContext);
  const CONNECTED_ACCOUNT_ID = settings?.ED_CONNECT_ID;
  //console.log('CONNECTED_ACCOUNT_ID',CONNECTED_ACCOUNT_ID);

  //const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_KEY), []);

  const [cartFees, setFees] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [roundUpAmt, setRoundUpAmt] = useState(0);

  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "CA",
  });

  const [manFormValid, setManFormValid] = useState(false);
  const [cartSubTotal, setCartSubTotal] = useState(null);

  //get the service fees and tax rate for this event
  useEffect(() => {
    async function fetchFees() {
      try {
        const res = await GetServiceFees(globalEventId);
        
        setFees(res);
        setTaxRate(res[0]?.EF_TAX_RATE || 0);
      } catch (error) {
        console.error('Error fetching fees:', error);
      } 
    }

    fetchFees();
  }, []);

  //get the cart contents
  useEffect(() => {

    if (globalCartId.cartID?.length > 0) {
            async function fetchCartContents() {
            const cartData = await GetCartContents(globalCartId.cartID);
            if (cartData.length === 0)
                return

            setCartInfo(cartData);
            setRoundUpAmt(cartData[0].charityAmt);

          }

        fetchCartContents();
      }
  }, [globalCartId.cartID]);

  useEffect(() => {
    if (cartInfo?.length > 0) {
      async function calcSubTotal() {
            const orderSubTotal = await calcCartValues(cartInfo, cartFees, taxRate);            
            setCartSubTotal(orderSubTotal.subTotal);
      }

      calcSubTotal();
    }

  }, [cartInfo]);

  //calculate cart subTotal and fetch secrets
  useEffect(() => {
    if (!hasFetched && cartSubTotal !== null) {
      async function fetchClientSecret() {
        
        try {
          if (cartSubTotal > 0) { 
            const clientSecretResult = await GetClientSecret(globalEventId, 'cad', globalCartId.cartID, promoCodeInput);
            setClientSecret(clientSecretResult);
          }
          else { 
            const clientSecretResult = await SetFreeSecret(globalEventId, 'cad', globalCartId.cartID, promoCodeInput);
            setClientSecret(clientSecretResult);
          }

        } catch (error) {
          console.error('Failed to fetch client secret:', error.message);
          setClientSecret(null);
        } finally {
          setHasFetched(true); // ensure we only try once
        }
      }

      fetchClientSecret();
    }
  }, [hasFetched, cartSubTotal]);

  //validate all non-stripe checkout fields are populated.
  useEffect(() => {
    const allFieldsFilled =
      email.trim() !== '' &&
      confEmail.trim() !== '' &&
      customerName.trim() !== '' &&
      phone.trim() !== '' &&
      address.line1.trim() !== '' &&
      address.city.trim() !== '' &&
      address.state.trim() !== '' &&
      address.postal_code.trim() !== '';

    const emailsMatch = email === confEmail;

    setManFormValid(allFieldsFilled && emailsMatch);
  }, [email, confEmail, customerName, phone, address]);

  const handleFreeOrderSubmit = async () => {
    //setSubmitting(true);
    //setError(null);
    if (!manFormValid || isSubmitting) return;
      setIsSubmitting(true);

    try {
      const payload = {
        email,
        customerName,
        phone,
        address,
        globalEventId,
        clientSecret
      };

      const res = await SubmitFreeOrder(payload);

      if (res.success) {
              SetGlobalCartId({
        cartID: '',
        cartContents: new Map(),
      });

      updatePromoCode(globalEventId,'');

      stopTimer();
      resetTimer();

      localStorage.removeItem('promoCode');
      localStorage.removeItem('cartID');
      navigate('/order-results?orderSuccess=true');
      } else {
        console.log(res.message || "Failed to submit promo order.");
      }
    } catch (err) {
      console.log("Error submitting promo order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const stripePromise = useMemo(() => {
    // always load Stripe; only scope to the connected account if we have one
    const opts = CONNECTED_ACCOUNT_ID
      ? { stripeAccount: CONNECTED_ACCOUNT_ID }
      : {};

    return loadStripe(process.env.REACT_APP_STRIPE_KEY, opts);
  }, [CONNECTED_ACCOUNT_ID]);

  const appearance = {
    theme: "stripe", // Options: 'stripe', 'night', 'flat', 'none',
    variables: {
      fontWeightLabel: '700',
    },
    rules: {
      '.Label': {
        fontWeight: 'bold',
      },
    },
  };

  const validateEmails = () => {
    if (email !== confEmail) {
      setEmailError("Email addresses do not match.");
    } else {
      setEmailError("");
    }
  };
  
  // Trigger validation when either email field changes
  useEffect(() => {
    validateEmails();
  }, [email, confEmail]);
 

  // Check if the emails match
  const emailMismatch = email !== confEmail;
  const emailsAreValid = email.length > 0 && confEmail.length > 0 && email === confEmail;


  const loader = 'auto';

  return (
    <>
      <Container className="mt-2">
        <Row>
          <Col className='text-start'>
            <Link to="/cart" style={{ textDecoration: 'none', fontSize:'12px' }}>{'<< Back to cart'}</Link>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <h3>Checkout</h3>
          </Col>
        </Row>
        <Row >
        <Col>
          <ReviewSection 
            cartInfo={cartInfo} 
            promoCode={promoCodeInput} 
            cartFees={cartFees} 
            taxRate={taxRate}
            showRoundUp={settings?.ES_CHARITY_ID}
            roundUpAmt={roundUpAmt}/>
        </Col>
      </Row>

    <Row>
      <Col>
      {!hasFetched ? (
  // Show spinner while clientSecret is not set
  <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
  ) : grandTotal == 0 ? (
    <>
          <form autoComplete="off" name="stripe-checkout-form">

              <h3 className="text-start">Billing</h3>
              <div>
                  <Form.Label className="text-start w-100">Email address</Form.Label>
                  <Form.Control required type="email" autoComplete="new-email" placeholder="" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={emailMismatch}/>
                  {emailMismatch && <Form.Control.Feedback type="invalid">Emails do not match</Form.Control.Feedback>}

                  <Form.Label className="text-start w-100">Confirm Email address</Form.Label>
                  <Form.Control required type="email" autoComplete="new-email-confirm" placeholder="" value={confEmail}
                  onChange={(e) => setConfEmail(e.target.value)}
                  isInvalid={emailMismatch}/>
                  {emailMismatch && <Form.Control.Feedback type="invalid">Emails do not match</Form.Control.Feedback>}
            </div>
            <div autoComplete="off" name="address-autofill-blocker">
                  <Form.Group>
                    <Form.Label className="text-start w-100">Full Name</Form.Label>
                    <Form.Control required value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="text-start w-100">Phone Number</Form.Label>
                    <Form.Control required
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="text-start w-100">Street Address</Form.Label>
                    <Form.Control required
                      value={address.line1}
                      onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="text-start w-100">City</Form.Label>
                    <Form.Control required
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="text-start w-100">Province / State</Form.Label>
                    <Form.Control required
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="text-start w-100">Postal Code</Form.Label>
                    <Form.Control required
                      value={address.postal_code}
                      onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                    />
                  </Form.Group>

                  <Button type='button'
                    disabled={!manFormValid || isSubmitting}
                    onClick={handleFreeOrderSubmit}
                    className='btn-atc mt-3 mb-5'>
                    {
                      isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Processing...
                      </>
                    ) : (
                      'Checkout'
                    )}
                  </Button>

            </div>
          </form>
  </>
  ) : (
    stripePromise && clientSecret && (
        <Elements options={{clientSecret, appearance}} stripe={stripePromise} >

          <form autoComplete="off" name="stripe-checkout-form">

              <h3 className="text-start">Billing</h3>
              <div>
                  <Form.Label className="text-start w-100">Email address</Form.Label>
                  <Form.Control type="email" autoComplete="new-email" placeholder="" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={emailMismatch}/>
                  {emailMismatch && <Form.Control.Feedback type="invalid">Emails do not match</Form.Control.Feedback>}

                  <Form.Label className="text-start w-100">Confirm Email address</Form.Label>
                  <Form.Control type="email" autoComplete="new-email-confirm" placeholder="" value={confEmail}
                  onChange={(e) => setConfEmail(e.target.value)}
                  isInvalid={emailMismatch}/>
                  {emailMismatch && <Form.Control.Feedback type="invalid">Emails do not match</Form.Control.Feedback>}
            </div>
            <div autoComplete="off" name="address-autofill-blocker">
              <AddressElement options={{
                mode: 'billing',
                  allowedCountries: ["CA", "US"],
                  autocomplete: { mode: "automatic" }, // Enable address suggestions
                  fields: {
                    phone: "always", // Always show phone number field
                  },
                  validation:{phone:{required:"always"}}
                }} 
              />
              </div>
          </form>
          <CheckoutForm email={email} confEmail={confEmail} cartInfo={cartInfo} cartID={globalCartId.cartID} promoCodeInput={promoCodeInput} updatePromoCode={updatePromoCode} formIsValid={emailsAreValid}/>
    </Elements>
    )
      )}
  </Col>
  </Row>
  </Container>
  </>
)};

function ReviewSection ({cartInfo, promoCode,cartFees,taxRate,showRoundUp,roundUpAmt}) {
  const [checkoutValues, setCheckoutValues] = useState({
    subTotal: 0,
    serviceFees: 0,
    grandTotal: 0,
    ticketQty: 0
  });

  useEffect(() => {
    async function fetchCartValues() {
      const result = await calcCartValues(cartInfo, cartFees, taxRate);
      setCheckoutValues(result);
    }

    if (cartInfo.length > 0) {
      fetchCartValues();
    }
  }, [cartInfo, cartFees, taxRate]);

  const { subTotal, serviceFees, grandTotal, ticketQty } = checkoutValues;

  let dateBlock = '';
  let header = '';
  let ticketDate = '';
  let ticketTime = '';

  return (
    <>
      <h3 className="text-start">Review</h3>
      {cartInfo.filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5).length > 0 && (
      <Row className="mb-4">
      <Col xs={12} className="mb-3">
          <Card>
          <Card.Body>
              <Card.Title className="text-start">Tickets</Card.Title>
              {cartInfo
              .filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5)
              .map((item,index) => {
                  const itemKey = `ticket-${item.id || index}`;
                  item.rowId = `1-${index}`;

                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;

                  const displayPrice = item?.replacement != null
                    ? Number(itemPrice)
                    : getAllInPrice(itemPrice, item.ticket_type, cartFees);
                  itemPrice = displayPrice.toFixed(2);
                  lineTotal = (displayPrice * item.cart_qty).toFixed(2);

                  if (item.ticket_type === 1 || item.ticket_type === 5) {
                    if (dateBlock != item.EVENT_DATE) {
                        dateBlock = item.EVENT_DATE;
                        ticketDate = item.EVENT_DATE.substring(0, 10); 
                        ticketTime = convertTo12HourFormat(item.EVENT_DATE.substring(11, 16)); 
                        header = ticketHeader(ticketDate,ticketTime);
                    }
                    else
                        header = '';
                  }
                  else
                    header = '';

                  return (
                    <React.Fragment key={itemKey}>
                  {header}
                  <Row key={`row-${itemKey}`}>
                      <Col xs={4} className="text-start cartColDetail">{item.NAME}</Col>
                      <Col xs={2} className="cartColDetail">{item.cart_qty}</Col>
                      <Col xs={3} className="text-end cartColDetail">${itemPrice}</Col>
                      <Col xs={3} className="text-end cartColDetail">${lineTotal}</Col>
                  </Row>
                  </React.Fragment>
                  );
              })}
          </Card.Body>
          </Card>
      </Col>
      </Row>
    )}

    {/* Passes Section */}
    {cartInfo.filter((item) => item.ticket_type === 2).length > 0 && (
      <Row className="mb-4">
      <Col xs={12} className="mb-3">
          <Card>
          <Card.Body>
              <Card.Title className="text-start">Passes</Card.Title>
              <Row>
              <Col xs={4} className="text-start fw-bold cartColHeading">Item</Col>
              <Col xs={2} className="text-center fw-bold cartColHeading">Qty</Col>
              <Col xs={3} className="text-end fw-bold cartColHeading">Price</Col>
              <Col xs={3} className="text-end fw-bold cartColHeading">Total</Col>
              </Row>

              {cartInfo
              .filter((item) => item.ticket_type === 2)
              .map((item,index) => {
                  
                  item.rowId = '2-'+index;
                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;

                  itemPrice = itemPrice.toFixed(2);
                  lineTotal = lineTotal.toFixed(2);

                  //don't charge service fees on replacement tickets
                  /*if (item.ticket_type === 2 && item?.replacement === null)
                    ticketQty += item.cart_qty
                  */
                  return (
                  <Row key={2-index}>
                      <Col xs={4} className="text-start cartColDetail">{item.NAME}</Col>
                      <Col xs={2} className="text-center cartColDetail">{item.cart_qty}</Col>
                      <Col xs={3} className="text-end cartColDetail">${itemPrice}</Col>
                      <Col xs={3} className="text-end cartColDetail">${lineTotal}</Col>
                  </Row>
                  );
              })}
          </Card.Body>
          </Card>
      </Col>
      </Row>
    )}

    {/* Add Ons Section */}
    {cartInfo.filter((item) => item.ticket_type === 3).length > 0 && (
      <Row className="mb-4">
      <Col xs={12} className="mb-3">
          <Card>
          <Card.Body>
              <Card.Title className="text-start">Add Ons</Card.Title>
              <Row>
              <Col xs={4} className="text-start fw-bold cartColHeading">Item</Col>
              <Col xs={2} className="text-center fw-bold cartColHeading">Qty</Col>
              <Col xs={3} className="text-end fw-bold cartColHeading">Price</Col>
              <Col xs={3} className="text-end fw-bold cartColHeading">Total</Col>
              </Row>

              {
              cartInfo
              .filter((item) => item.ticket_type === 3)
              .map((item,index) => {
                  item.rowId = '3-'+index;
                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;

                  itemPrice = itemPrice.toFixed(2);
                  lineTotal = lineTotal.toFixed(2);

                  return (
                  <Row key={3-index}>
                      <Col xs={4} className="text-start cartColDetail">{item.NAME}</Col>
                      <Col xs={2} className="text-center cartColDetail">{item.cart_qty}</Col>
                      <Col xs={3} className="text-end cartColDetail">${itemPrice}</Col>
                      <Col xs={3} className="text-end cartColDetail">${lineTotal}</Col>
                  </Row>
                  );
              })}
          </Card.Body>
          </Card>
      </Col>
      </Row>
    )}

  {/* Cart Summary */}
  <Row className="mb-4">
      <Col>
      <Card>
          <Card.Body>
          <Row>
              <Col xs={9} className="text-end fw-bold cartColHeading">
              Subtotal (incl. fees):
              </Col>
              <Col xs={3} className="text-end cartColDetail">
              ${(subTotal + serviceFees).toFixed(2)}
              </Col>
          </Row>
          {/*<Row>
              <Col xs={9} className="text-end fw-bold cartColHeading">
              Service Fees:
              </Col>
              <Col xs={3} className="text-end cartColDetail">${serviceFees.toFixed(2)}</Col>
          </Row>*/}
          <Row>
              <Col xs={9} className="text-end fw-bold cartColHeading">
              Tax:
              </Col>
              <Col xs={3} className="text-end cartColDetail">
              ${((subTotal + serviceFees) * taxRate).toFixed(2)}
              </Col>
          </Row>
                {(showRoundUp) && (
                  <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Round Up:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    {(Number(roundUpAmt ?? 0)).toFixed(2)}
                  </Col>
                </Row>
                )}
          <Row>
              <Col xs={9} className="text-end fw-bold cartColHeading">
              Grand Total:
              </Col>
              <Col xs={3} className="text-end cartColDetail">
              ${(grandTotal)}
              </Col>
          </Row>
          </Card.Body>
      </Card>
      </Col>
  </Row>
  </>
)

}

function ticketHeader(inDate,inTime) {
  return (
      <>
      <Row>
      <Col className='text-start'>
          {inDate}, {inTime}
      </Col>
  </Row>
  <Row>
    <Col xs={4} className='text-start fw-bold cartColHeading'>Ticket</Col>
    <Col xs={2} className='text-center fw-bold cartColHeading'>Qty</Col>
    <Col xs={3} className='text-end fw-bold cartColHeading'>Price</Col>
    <Col xs={3} className='text-end fw-bold cartColHeading'>Total</Col>
  </Row>
  </>
  )
}

async function calcCartValues(cartInfo,cartFees,taxRate) {

  let subTotal = 0;
  let ticketQty = 0;
  let serviceFees = 0;
  let roundUp = cartInfo?.[0]?.charityAmt ?? 0;

                cartInfo
              .filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5)
              .forEach((item,index) => {
                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;

                  //don't charge service fees on replacement tickets
                  if (item.ticket_type === 1 && item?.replacement === null) {
                    ticketQty += item.cart_qty
                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          if (itemPrice > 0)
                            serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          if (itemPrice > 0)
                            serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }                  
                  } else if ((item.ticket_type === 5) && item?.replacement === null) {
                      ticketQty += item.cart_qty;
                      const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_TYPE;
                      if (serviceFeeType === 1) {
                          if (itemPrice > 0)
                              serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * item.cart_qty;
                      } else if (serviceFeeType === 2) {
                          if (itemPrice > 0)
                              serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * lineTotal / 100;
                      }
                  }

                  subTotal = subTotal + lineTotal;

                  itemPrice = itemPrice.toFixed(2);
                  lineTotal = lineTotal.toFixed(2);

                  //if the subtotal for regular tickets is $0 then don't charge any service fees on them.
                  if (subTotal == 0)
                  serviceFees = 0;

              });

              cartInfo
              .filter((item) => item.ticket_type === 2 )
              .forEach((item,index) => {
                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;
                  subTotal = subTotal + lineTotal;

                  //don't charge service fees on replacement tickets
                  if (item.ticket_type === 2 && item?.replacement === null) {
                    ticketQty += item.cart_qty
                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }                  
                  }

                  itemPrice = itemPrice.toFixed(2);
                  lineTotal = lineTotal.toFixed(2);

                  //if the subtotal is $0 then don't charge any service fees.
                  if (subTotal == 0)
                      serviceFees = 0;

              });

              cartInfo
              .filter((item) => item.ticket_type === 3)
              .forEach((item,index) => {
                  let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                  let lineTotal = itemPrice * item.cart_qty;
                  subTotal = subTotal + lineTotal;

                  itemPrice = itemPrice.toFixed(2);
                  lineTotal = lineTotal.toFixed(2);

                  //don't charge service fees on replacement tickets
                  if (item.ticket_type === 3 && item?.replacement === null) {
                    //ticketQty += item.cart_qty
                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }                  
                  }
                  
                  //if the subtotal is $0 then don't charge any service fees.
                  if (subTotal == 0)
                    serviceFees = 0;

              })
              
              grandTotal = (subTotal + ((subTotal + serviceFees) * taxRate) + serviceFees + roundUp).toFixed(2);

              return {subTotal:subTotal, serviceFees:serviceFees, ticketQty:ticketQty, grandTotal:grandTotal}

}

export default CheckoutPage;
